import React, { memo, useMemo, useEffect, useCallback, Fragment } from 'react';
import { ModalDrawer, ModalHeader, ModalContent, ModalAction, ModalFooter } from '@core/components/Modal';
import { css } from '@emotion/react';
import { useBloc } from '@core/utils/bloc';
import { useObservable, useNonNilObservable } from '@core/utils/hooks/rxjs';
import { formGroup, formControl, formArray, FormDataType, useFormConfig, useFormState } from '@core/utils/form';
import { CommonService } from '@business/services';
import { FormSelect, FormInput, FormTimeInput, FormToggleInput, FormCheckbox } from '@core/components/form';
import { Session } from '@modules/auth/session';
import { UserSocialId, UserSocialAccountResponse } from '@business/entities/account';
import { R } from '@core/utils/r';
import { AddSchedularModalBloc } from './AddSchedularModalBloc';
import { Row, Col, CardBody, CardTitle } from 'reactstrap';
import { FormArrayInputGroup } from '@core/components/form/FormArrayInput';
import { useModalInstance } from '@core/components/ModalStack';
import { tap } from 'rxjs/operators';
import { CBody } from '@modules/common/CBody';
import { ScheduleResponse } from '@business/entities/calendar';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import { SchedulingReminders } from '../EditMyScheduleModal/SchedulingReminders';
import { SchedulingFields } from '../EditMyScheduleModal/SchedulingFields';

export enum MeetingType {
  collective = 'collective',
  nextAvailable = 'round-robin-maximize-fairness',
  roundRobin = 'round-robin-maximize-availability',
}

function defineReminderForm() {
  return formGroup({
    delivery_method: formControl<string>({
      initialValue: 'email',
    }),
    delivery_recipient: formControl<string>({
      initialValue: 'customer',
      label: 'Email Receipient',
    }),
    email_subject: formControl<string>({
      initialValue: 'Your meeting is coming up!',
      label: 'Email Subject',
      required: true,
    }),
    time_before_event: formControl<number>({
      initialValue: 15,
      label: 'Time Before Event',
      required: true,
    }),
  });
}

function defineFieldsForm() {
  return formGroup({
    label: formControl<string>({
      label: 'Label',
      required: true,
    }),
    type: formControl<string>({
      initialValue: 'text',
      label: 'Type',
      required: true,
    }),
    required: formControl<boolean>({
      initialValue: false,
      label: 'Required',
    }),
  });
}

function defineOpeningForm() {
  return formGroup({
    days: formControl<string[]>({
      initialValue: ['M', 'T', 'W', 'R', 'F'],
      required: true,
    }),
    start: formControl<string>({
      initialValue: '09:00',
      required: true,
    }),
    end: formControl<string>({
      initialValue: '17:00',
      required: true,
    }),
  });
}

function defineSchedulingForm() {
  return formGroup({
    title: formControl<string>({
      label: 'Event Name',
      required: true,
    }),
    duration: formControl<number>({
      initialValue: 30,
      label: 'Duration',
      required: true,
    }),
    minBookingNotice: formControl<number>({
      initialValue: 2,
      label: 'Min Booking Notice',
      required: true,
    }),
    futureLimit: formControl<number>({
      initialValue: 2,
      label: 'Future Limit',
      required: true,
    }),
    bufferTime: formControl<number>({
      initialValue: 30,
      label: 'Buffer Time',
      required: true,
    }),

    opening: formArray(() => defineOpeningForm(), {
      initialValue: [{ start: '09:00', end: '17:00', days: ['M', 'T', 'W', 'R', 'F'] }],
    }),
    customHours: formControl<boolean>({
      label: 'Custom Hours',
      initialValue: false,
    }),

    accounts: formControl<string[]>({
      label: 'Calendar Availability',
      initialValue: R.compact([R.get(Session.user, 'socialAccounts[0].id')]),
      required: true,
    }),
    meetingType: formControl<MeetingType>({
      label: 'Group Meeting Type',
      initialValue: MeetingType.roundRobin,
    }),
    reminders: formArray(() => defineReminderForm()),
    fields: formArray(() => defineFieldsForm()),
    deal: formControl<boolean>({
      label: 'Create Deal on Booking',
      initialValue: false,
    }),
  });
}

export type SchedularForData = FormDataType<typeof defineSchedulingForm>;
function toFormData(schedule: ScheduleResponse, socials: UserSocialAccountResponse[], deal = false): Partial<SchedularForData> {
  const accounts = socials.filter(s => schedule.account_ids.find(a => a === s.accountId)).map(s => s.id);
  const firstAccount = R.first(schedule.config.booking.opening_hours)?.account_id;
  const hours = R.filter(schedule.config.booking.opening_hours, s => s.account_id == firstAccount);
  return {
    title: schedule.config.event.title,
    duration: schedule.config.event.duration,
    bufferTime: schedule.config.booking.min_buffer,
    minBookingNotice: schedule.config.booking.min_booking_notice / 60,
    futureLimit: schedule.config.booking.available_days_in_future / 7,
    meetingType: schedule.config.booking.scheduling_method,
    opening: hours.map(o => ({ days: o.days, start: o.start, end: o.end })),
    accounts: accounts,
    customHours: schedule.customHours || false,
    reminders: schedule.config.reminders,
    fields: schedule.config.fields,
    deal,
  };
}

const styles = {
  content: css`
    padding: 25px !important;
    background-color: var(--body-bg-color);
  `,
};

export interface AddSchedularModalProps {
  socialId: UserSocialId;
  onDone(): void;
  schedule?: ScheduleResponse;
  scheduleId?: string;
  deal?: boolean;
}

export const AddSchedularModal = memo((props: AddSchedularModalProps) => {
  const { schedule, scheduleId, deal = false } = props;
  const form = useMemo(() => defineSchedulingForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const openings = useNonNilObservable(form.controls.opening.items$);
  const bloc = useBloc(AddSchedularModalBloc);
  const socials = useObservable(bloc.social$, []);
  const user = useObservable(Session.user$);
  const modal = useModalInstance();
  const socialItems = useMemo(() => {
    return CommonService.selectItems(socials, s => `${s.name} (${s.email})`, 'id');
  }, [socials]);

  const isEdit = useMemo(() => !!schedule, [schedule]);

  useEffect(() => {
    if (schedule && socials.length > 0) {
      form.patchValue(toFormData(schedule, socials, deal));
    }
  }, [form, schedule, socials, deal]);

  useEffect(() => {
    if (formValue.customHours && formValue.accounts.length > 1) {
      form.patchValue({ customHours: false, opening: [] });
    }
  }, [form, formValue]);

  useEffect(() => {
    if (formValue.accounts.length == 1 && formValue.meetingType != MeetingType.roundRobin) {
      form.patchValue({ meetingType: MeetingType.roundRobin });
    }
  }, [form, formValue]);

  const bufferTimeItems = useMemo(() => CommonService.selectItems([0, 15, 30, 45, 60, 90, 120], i => `${i} minutes`), []);
  const durationItems = useMemo(() => CommonService.selectItems([15, 30, 45, 60, 90, 120], i => `${i} minutes`), []);
  const futureLimitItems = useMemo(() => CommonService.selectItems([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], i => `${i} weeks`), []);
  const minBookingNoticeItems = useMemo(() => CommonService.selectItems([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], i => `${i} hour`), []);

  const onSubmit = useCallback(
    (data: SchedularForData) => {
      return bloc.createSchedule(props.socialId, data, scheduleId).pipe(
        tap(() => props.onDone()),
        tap(() => modal.close()),
      );
    },
    [bloc, modal, props, scheduleId],
  );
  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  return (
    <ModalDrawer maxWidth="lg">
      <ModalHeader>
        <div className="d-flex flex-row" style={{ justifyContent: 'start', alignItems: 'center' }}>
          <div style={{ display: 'flex', flex: 1, flexDirection: 'row', alignItems: 'center', height: 50 }}>Scheduling Page</div>
        </div>
      </ModalHeader>
      <ModalContent customCss={styles.content}>
        <CBody>
          <FormInput
            autoFocus
            control={form.controls.title}
            description="This is the name of the event that will show up on the calendar for the guests and any users that this meeting gets scheduled for."
          />
          <Row>
            <Col>
              <FormSelect control={form.controls.duration} items={durationItems} description="The length of each calendar event" />
            </Col>
            <Col>
              <FormSelect
                control={form.controls.minBookingNotice}
                items={minBookingNoticeItems}
                description="How far in advance events must be scheduled."
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormSelect
                control={form.controls.futureLimit}
                items={futureLimitItems}
                description="How far in advance events are allowed to be scheduled."
              />
            </Col>
            <Col>
              <FormSelect
                control={form.controls.bufferTime}
                items={bufferTimeItems}
                description="A buffer time in between events on your calendar."
              />
            </Col>
          </Row>

          <FormSelect type="multiple" control={form.controls.accounts} items={socialItems} description="Check calendar availability for users" />
          {formValue.accounts.length > 1 && (
            <Fragment>
              <FormSelect
                control={form.controls.meetingType}
                items={CommonService.selectItems([MeetingType.roundRobin, MeetingType.nextAvailable, MeetingType.collective], i =>
                  CommonService.getMeetingTypeLabel(i),
                )}
              />

              <div className="mb-4">
                <div>
                  <CardTitle>Round Robin</CardTitle>
                  <span>Round robin scheduling ensures appointments are distributed evenly among everyone.</span>
                </div>
                <div className="mt-2">
                  <CardTitle>Next Available</CardTitle>
                  <span>
                    Next available scheduling maximizes availability when it ensures that event bookings occur more frequently for hosts that have
                    more availability on their calendars.
                  </span>
                </div>
                <div className="mt-2">
                  <CardTitle>All Available</CardTitle>
                  <span>
                    All available scheduling pools your team member&apos;s availability to make sure your teammembers are all free when an invitee
                    selects a time to meet.
                  </span>
                </div>
                <hr />
              </div>
            </Fragment>
          )}

          <FormRadioGroup control={form.controls.customHours}>
            <FormRadio label={'Working Hours'} value={false} />
            <FormRadio label={'Custom Openings'} disabled={formValue.accounts.length > 1} value={true} />
          </FormRadioGroup>

          {formValue.customHours && (
            <FormArrayInputGroup
              alwaysEditable
              withoutOrder
              label=""
              form={form.controls.opening}
              onControl={inForm => (
                <div className="d-flex flex-row align-items-center">
                  <FormToggleInput
                    style={{ width: 300 }}
                    withoutGroup
                    control={inForm.controls.days as any}
                    items={[
                      { label: 'S', value: 'U' },
                      { label: 'M', value: 'M' },
                      { label: 'T', value: 'T' },
                      { label: 'W', value: 'W' },
                      { label: 'T', value: 'R' },
                      { label: 'F', value: 'F' },
                      { label: 'S', value: 'S' },
                    ]}
                  />

                  <span style={{ width: 15 }}></span>
                  <FormTimeInput withoutGroup control={inForm.controls.start} />
                  <span className="text-muted ml-2 mr-2">until</span>
                  <FormTimeInput withoutGroup control={inForm.controls.end} />
                </div>
              )}
              onLabel={() => ''}
            />
          )}
          <div className="mt-2">
            <FormCheckbox control={form.controls.deal} />
          </div>
          <hr />
          <CardTitle>Reminders</CardTitle>
          <SchedulingReminders form={form.controls.reminders} />
          <hr />
          <CardTitle>Additional Fields</CardTitle>
          <SchedulingFields form={form.controls.fields} />
        </CBody>
      </ModalContent>
      <ModalFooter>
        <ModalAction intent="primary" text={'Save'} loading={submitting} autoClose={false} onClick={form.submit} />
      </ModalFooter>
    </ModalDrawer>
  );
});
